import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';

import { payorProviderCredentialsActions } from '../../../../store';
import { useProviderDetailsState } from './useProviderDetailsState';

import type { AppDispatch } from '../../../../store';
import type { IPayorProviderCredential } from '../../../../utils/types';
import type { PayorCredentialEditorsParams } from './types';

export function usePayorCredentialEditors({
  payorCredentials,
  setPayorCredentials,
  setEditableCredentialIndex,
  setIsNewCredentialAdding,
}: PayorCredentialEditorsParams) {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { providerId } = useParams();
  const { provider } = useProviderDetailsState();

  const refreshCredentials = () => {
    dispatch(
      payorProviderCredentialsActions.getList({
        params: {
          provider_id: providerId,
        },
        getAccessTokenSilently,
      }),
    );
  };

  const handleAddCredential = () => {
    setPayorCredentials([
      {
        id: null,
        provider: provider,
        payor: null,
        date_start: null,
        date_end: null,
      },
      ...payorCredentials,
    ]);
    setEditableCredentialIndex(0);
    setIsNewCredentialAdding(true);
  };

  const handleEditCredential = (index: number) => {
    setEditableCredentialIndex(index);
  };

  const handleUpdateCredential = async (updatedCredential: IPayorProviderCredential) => {
    try {
      const credentialData = {
        payor_id: updatedCredential.payor?.id,
        provider_id: updatedCredential.provider?.id,
        date_start: updatedCredential.date_start,
        date_end: updatedCredential.date_end,
      };
      const response = updatedCredential.id
        ? await dispatch(
            payorProviderCredentialsActions.updatePayorProviderCredential({
              data: {
                date_start: updatedCredential.date_start,
                date_end: updatedCredential.date_end,
              },
              payorProviderCredId: updatedCredential.id,
              getAccessTokenSilently,
            }),
          )
        : await dispatch(
            payorProviderCredentialsActions.createPayorProviderCredential({
              data: credentialData,
              getAccessTokenSilently,
            }),
          );

      if (!response.error) {
        refreshCredentials();
        setEditableCredentialIndex(null);
        setIsNewCredentialAdding(false);
      }
    } catch (error) {
      toast(`Error updating/creating credential:${error}`);
    }
  };

  const handleDeleteCredential = async (credential: IPayorProviderCredential) => {
    if (!credential.id) return;
    const { error } = await dispatch(
      payorProviderCredentialsActions.deletePayorProviderCredential({
        payorProviderCredId: credential.id,
        getAccessTokenSilently,
      }),
    );
    if (!error) {
      refreshCredentials();
    }
  };

  const handleCancelEditCredential = () => {
    setPayorCredentials((credentials) =>
      credentials.filter((credential) => credential.id !== null),
    );
    setEditableCredentialIndex(null);
    setIsNewCredentialAdding(false);
  };

  return {
    handleAddCredential,
    handleEditCredential,
    handleCancelEditCredential,
    handleUpdateCredential,
    handleDeleteCredential,
  };
}
